import React from 'react';
import { useNavigate } from 'react-router-dom';

const WORK_TYPES = [
  'Photograph / Image',
  'Written Article / Blog Post',
  'Video / Film',
  'Music / Audio',
  'Software / Code',
  'Artwork / Illustration',
  'Other',
];

export function DMCAForm({
  user, usageStats, planTracker, formData, setFormData,
  isProcessing, processingError, onSubmit, showSignInPrompt,
}) {
  const navigate = useNavigate();
  const handle = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

  const isFormValid =
    formData.claimantName.trim() && formData.claimantEmail.trim() &&
    formData.workDescription.trim() && formData.originalWorkUrl.trim() &&
    formData.infringingUrl.trim() && formData.platform.trim();

  const limitReached = user && usageStats && !usageStats.canGenerate;
  const currentPlan  = planTracker?.planConfig?.name || 'Free';

  return (
    <div className="form-page">
      {showSignInPrompt && (
        <div className="sign-in-banner">
          <div className="sign-in-content">
            <span>Sign in to save your DMCA notice history</span>
            <button className="sign-in-banner-btn" onClick={() => navigate('/')}>Sign In</button>
          </div>
        </div>
      )}

      <div className="form-page-inner">

        {/* HERO */}
        <div className="form-hero">
          <div className="form-hero-left">
            <div className="form-eyebrow">
  <span className="eyebrow-pulse" />
  17 U.S.C. § 512 Compliant
</div>
<h1 className="form-title">Generate, send & enforce <em>your copyright</em></h1>
<p className="form-subtitle">We generate your legal notice, find the platform's DMCA agent, and hand you a ready-to-send email.</p>
          </div>
          
        </div>

        {/* TOP TWO COLUMNS */}
        <div className="form-top-grid">

          {/* LEFT — About You */}
          <div className="form-panel">
            <div className="form-panel-header">
              <span className="form-panel-dot" />
              <span className="form-panel-title">About you</span>
            </div>
            <div className="form-panel-body">
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Full name</label>
                  <input className="form-input" type="text" placeholder="Jane Smith"
                    value={formData.claimantName} onChange={handle('claimantName')} disabled={isProcessing} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email address</label>
                  <input className="form-input" type="email" placeholder="jane@example.com"
                    value={formData.claimantEmail} onChange={handle('claimantEmail')} disabled={isProcessing} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  Mailing address <span className="form-label-optional">(optional)</span>
                </label>
                <input className="form-input" type="text" placeholder="123 Main St, City, State, ZIP"
                  value={formData.claimantAddress} onChange={handle('claimantAddress')} disabled={isProcessing} />
              </div>
            </div>
          </div>

          {/* RIGHT — Original Work */}
          <div className="form-panel">
            <div className="form-panel-header">
              <span className="form-panel-dot" />
              <span className="form-panel-title">Your original work</span>
            </div>
            <div className="form-panel-body">
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Type of work</label>
                  <select className="form-input form-select" value={formData.workType}
                    onChange={handle('workType')} disabled={isProcessing}>
                    {WORK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Brief description</label>
                  <input className="form-input" type="text" placeholder="My original photo taken in Paris, 2023"
                    value={formData.workDescription} onChange={handle('workDescription')} disabled={isProcessing} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">URL of your original work</label>
                <input className="form-input" type="url" placeholder="https://yoursite.com/original-work"
                  value={formData.originalWorkUrl} onChange={handle('originalWorkUrl')} disabled={isProcessing} />
              </div>
            </div>
          </div>
        </div>

        {/* INFRINGEMENT — full width */}
        <div className="form-panel">
          <div className="form-panel-header">
            <span className="form-panel-dot" />
            <span className="form-panel-title">The infringement</span>
          </div>
          <div className="form-panel-body">
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">URL of infringing content</label>
                <input className="form-input" type="url" placeholder="https://example.com/stolen-content"
                  value={formData.infringingUrl} onChange={handle('infringingUrl')} disabled={isProcessing} />
              </div>
              <div className="form-group">
                <label className="form-label">Platform / host</label>
                <input className="form-input" type="text" placeholder="Google, Cloudflare, WordPress…"
                  value={formData.platform} onChange={handle('platform')} disabled={isProcessing} />
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER BAR */}
        <div className="form-footer-bar">
          <div className="form-footer-left">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Legally structured under 17 U.S.C. § 512 · Notice generated instantly
          </div>
          <button
            className="btn-generate"
            onClick={onSubmit}
            disabled={isProcessing || !isFormValid || limitReached}
          >
            {isProcessing ? (
              <><div className="spinner-small" /> Generating…</>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Generate notice
              </>
            )}
          </button>
        </div>

        {limitReached && (
          <div className="limit-upgrade-card">
            <div className="limit-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="limit-content">
              <h4>You've reached your {currentPlan} plan limit</h4>
              <p>Upgrade to generate more DMCA notices.</p>
            </div>
            <button className="upgrade-btn-card" onClick={() => navigate('/pricing-plans')}>
              Upgrade Now
            </button>
          </div>
        )}

        {processingError && !limitReached && (
          <div className="error-message">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {processingError}
          </div>
        )}
      </div>
    </div>
  );
}